/**
 * Hotel Filter Count API
 *
 * Fetches filter counts for hotel search (price buckets, star ratings, amenities, etc.).
 * Called during hotel search to populate filter options with counts.
 */
import type {
  FilterCategory,
  FilterCountMCPRequest,
  FilterCountDeviceInfo,
  FilterCountLocationContext,
} from '@contracts';
import { HOTELS_ENDPOINTS } from '@/api/endpoints';
import { logger } from '@/shared/utils/logger';
import { generateUUID } from '@/shared/utils/uuid';
import { getOrCreateSessionId } from '@/shared/utils';
import { baseFetch, createApiError, getCBContext } from './booking-review/baseApi';

// ============================================
// Error Class
// ============================================

export const HotelFilterCountError = createApiError('HotelFilterCountError');

// ============================================
// Types
// ============================================

export interface HotelFilterCountRequestParams {
  readonly sessionId: string;
  readonly userId: string;
  readonly city: string;
}

export interface HotelFilterCountResponse {
  readonly type: 'hotels-filter-count';
  readonly success: boolean;
  readonly filters: FilterCategory[] | null;
  readonly error?: { code: string; message: string };
}

// ============================================
// Raw API Response Type
// ============================================

interface RawResponse {
  success?: boolean;
  filters?: FilterCategory[];
  error?: { code: string; message: string };
}

// ============================================
// Helper Functions
// ============================================

/**
 * Build the MCP request payload for filter count API
 * Note: No urlParams/headers wrapper - payload is sent directly
 */
export function buildFilterCountPayload(
  params: HotelFilterCountRequestParams,
): FilterCountMCPRequest {
  const { deviceId, authToken } = getCBContext();

  const deviceInfo: FilterCountDeviceInfo = {
    device_id: deviceId,
    app_ver: '1.0.0',
    device_type: 'DESKTOP',
    platform: 'DESKTOP',
  };

  const locationContext: FilterCountLocationContext = {
    city: params.city,
  };

  return {
    request_id: generateUUID(),
    conversation_id: getOrCreateSessionId(),
    uuid: params.userId,
    auth: authToken,
    device_info: deviceInfo,
    rephrased_query: `Get filter counts for hotels in ${params.city}`,
    intents: ['FILTER_COUNT'],
    location_context: locationContext,
  };
}

// ============================================
// API Function
// ============================================

/**
 * Fetch filter counts from the API
 * POST /hotels/filter-count?sessionId=...&userId=...
 */
export async function fetchHotelFilterCount(
  params: HotelFilterCountRequestParams,
): Promise<HotelFilterCountResponse> {
  const payload = buildFilterCountPayload(params);

  const { data, error } = await baseFetch<RawResponse>({
    endpoint: HOTELS_ENDPOINTS.FILTER_COUNT,
    queryParams: { sessionId: params.sessionId, userId: params.userId },
    body: payload,
    logTag: 'HotelFilterCountClient',
  });

  // Network/HTTP error
  if (error) {
    return { type: 'hotels-filter-count', success: false, filters: null, error };
  }

  // API returned error
  if (data?.success === false && data?.error) {
    logger.warn('[HotelFilterCountClient] API returned error:', data.error);
    return { type: 'hotels-filter-count', success: false, filters: null, error: data.error };
  }

  // Success
  const filters = data?.filters ?? null;
  logger.info('[HotelFilterCountClient] Success:', {
    filterCategories: filters?.length ?? 0,
  });

  return { type: 'hotels-filter-count', success: true, filters };
}
